import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import RoundProgress from '../../../components/admin/RoundProgress';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import { copyFinalRoundsToAuctions, fetchCalendar, importSchedule } from '../../../services/calendar';
import { formatDateRange } from '../../../utils/formatDate';
import { containers, page } from '../../../theme/commonStyles';

/**
 * Wat er als volgende moet gebeuren in een ronde. Elke rij heeft precies één
 * vervolgstap, zodat de administrator niet hoeft te weten in welke stap hij zat.
 */
function NextStep({ progress }) {
    if (progress.matchCount === 0) {
        return <Typography variant="body2" color="text.secondary">geen wedstrijden</Typography>;
    }
    if (progress.startedCount === 0) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    if (progress.step === 3) {
        return <Typography variant="body2" color="text.secondary">Afgerond</Typography>;
    }
    return (
        <Chip
            size="small"
            color="primary"
            label={progress.nextAction === 'sync' ? 'Scores ophalen' : 'Controleren'}
        />
    );
}

/**
 * Kalender & rondes: de echte UEFA-kalender van dit seizoen. Elke ronde doorloopt
 * dezelfde stappen — gespeeld, scores ophalen, controleren — en je klikt door naar
 * de wedstrijden om de cijfers na te lopen.
 *
 * Deze pagina verving de combinatie van dropdowns (toernooifase + speelronde) en
 * de losse knoppen "spelerspunten berekenen" en "spelersdata opslaan": de kalender
 * weet zelf welke rondes er zijn en waar ze staan.
 */
function AdminKalender() {
    const { seasonId, seasonName, isLocked } = useSeason();
    const navigate = useNavigate();
    const notify = useNotification();

    const [rounds, setRounds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setRounds(await fetchCalendar(seasonId));
        } catch (err) {
            console.error('Kon de kalender niet ophalen:', err);
            notify.error('De kalender van dit seizoen kon niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [seasonId, notify]);

    useEffect(() => { load(); }, [load]);

    // De ronde waar de administrator mee bezig is: de meest recente die gespeeld is
    // maar nog niet helemaal akkoord. Die krijgt nadruk in de lijst.
    const currentRoundId = rounds.find((round) => round.progress.step > 0 && round.progress.step < 3)?.id;

    const handleImport = async () => {
        setImporting(true);
        try {
            const schedule = await importSchedule(seasonId);
            const copied = await copyFinalRoundsToAuctions(seasonId);
            notify.success(`${schedule.message}\n${copied.message}`);
            await load();
        } catch (err) {
            console.error('Kalender bijwerken mislukt:', err);
            notify.error(err.message || 'De kalender kon niet bijgewerkt worden.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <Box>
            <SeasonBreadcrumb page="Kalender & rondes" />

            <Box sx={{ ...page.header, alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4">Kalender &amp; rondes</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        De echte UEFA-kalender van {seasonName}. Klik een ronde om de wedstrijden te
                        controleren; het resultaat geldt voor alle subleagues die op dit seizoen spelen.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    onClick={handleImport}
                    disabled={importing || isLocked}
                    startIcon={importing ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {importing ? 'Bezig…' : 'Kalender bijwerken'}
                </Button>
            </Box>

            {loading ? (
                <Box sx={page.loadingContainer}><CircularProgress /></Box>
            ) : rounds.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="body1">
                        Er is nog geen kalender voor dit seizoen. Werk hem bij om de rondes en
                        wedstrijden op te halen.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} sx={containers.glassContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Ronde</TableCell>
                                <TableCell>Speeldata</TableCell>
                                <TableCell align="right">Wedstr.</TableCell>
                                <TableCell sx={{ minWidth: 180 }}>Voortgang</TableCell>
                                <TableCell>Volgende stap</TableCell>
                                <TableCell sx={{ width: 40 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rounds.map((round) => (
                                <TableRow
                                    key={round.id}
                                    hover
                                    onClick={() => navigate(`/admin/season/${seasonId}/kalender/${round.id}`)}
                                    sx={{
                                        cursor: 'pointer',
                                        ...(round.id === currentRoundId
                                            ? { backgroundColor: 'rgba(254, 203, 0, 0.06)' }
                                            : {}),
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: round.id === currentRoundId ? 700 : 400 }}
                                        >
                                            {round.title}
                                        </Typography>
                                        {round.subtitle && (
                                            <Typography variant="caption" color="text.secondary">
                                                {round.subtitle}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDateRange(round.firstKickoff, round.lastKickoff)}</TableCell>
                                    <TableCell align="right">{round.progress.matchCount}</TableCell>
                                    <TableCell>
                                        <RoundProgress progress={round.progress} />
                                    </TableCell>
                                    <TableCell><NextStep progress={round.progress} /></TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>
                                        <ChevronRightIcon fontSize="small" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default AdminKalender;
